import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface MaintenanceRecord {
    id: string;
    user_id: string;
    maintenance_type_id: string;
    mileage_km: number;
    cost?: number;
    notes?: string;
    performed_at: string;
    created_at: string;
}

export const useMaintenanceRecords = () => {
    const { user } = useAuth();
    const [records, setRecords] = useState<MaintenanceRecord[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            setRecords([]);
            setLoading(false);
            return;
        }

        fetchRecords();
    }, [user]);

    const fetchRecords = async () => {
        try {
            const { data, error } = await supabase
                .from('maintenance_records')
                .select('*')
                .order('performed_at', { ascending: false });

            if (error) throw error;
            setRecords(data || []);
        } catch (error) {
            console.error('[useMaintenanceRecords] Error fetching records:', error);
        } finally {
            setLoading(false);
        }
    };

    const addRecord = async (
        maintenanceTypeId: string,
        mileageKm: number,
        cost?: number,
        notes?: string,
        performedAt?: string
    ) => {
        if (!user) return;

        try {
            const { data, error } = await supabase
                .from('maintenance_records')
                .insert([{
                    user_id: user.id,
                    maintenance_type_id: maintenanceTypeId,
                    mileage_km: mileageKm,
                    cost,
                    notes,
                    performed_at: performedAt || new Date().toISOString()
                }])
                .select()
                .single();

            if (error) throw error;

            setRecords(prev => [data, ...prev]);
            return data;
        } catch (error) {
            console.error('[useMaintenanceRecords] Error adding record:', error);
            throw error;
        }
    };

    const getLastRecordForType = (typeId: string): MaintenanceRecord | undefined => {
        return records
            .filter(r => r.maintenance_type_id === typeId)
            .sort((a, b) => b.mileage_km - a.mileage_km)[0];
    };

    return { records, loading, addRecord, getLastRecordForType, refresh: fetchRecords };
};
